"use client"

import * as React from "react"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { RouteAreaRate } from "@/utils/routeAreaRate"
import { Loader2 } from "lucide-react"
import axiosInstance from "@/utils/axiosInstance"

interface ServiceLocation {
  id: number
  name: string
  type: string
}

interface RouteAreaRateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  rate?: RouteAreaRate | null
  mode: "create" | "edit"
  onSuccess: () => void
}

export function RouteAreaRateDialog({
  open,
  onOpenChange,
  rate,
  mode,
  onSuccess,
}: RouteAreaRateDialogProps) {
  const [isCreating, setIsCreating] = React.useState(false)
  const [isUpdating, setIsUpdating] = React.useState(false)
  const [serviceLocations, setServiceLocations] = React.useState<ServiceLocation[]>([])

  const [formData, setFormData] = React.useState({
    pickup_area_id: "",
    dropoff_area_id: "",
    price: "",
    currency: "ALL",
    description: "",
  })

  const [errors, setErrors] = React.useState<Record<string, string>>({})

  const isLoading = isCreating || isUpdating

  // Fetch service locations for dropdowns
  const fetchServiceLocations = React.useCallback(async () => {
    try {
      const response = await axiosInstance.get('/route-areas')
      setServiceLocations(response.data || [])
    } catch (error) {
      console.error('Error fetching route areas:', error)
    }
  }, [])

  React.useEffect(() => {
    if (open) {
      fetchServiceLocations()
    }
  }, [open, fetchServiceLocations])

  // Reset form when rate changes or dialog opens
  React.useEffect(() => {
    if (open) {
      if (mode === "edit" && rate) {
        setFormData({
          pickup_area_id: rate.pickup_area_id.toString(),
          dropoff_area_id: rate.dropoff_area_id.toString(),
          price: rate.price.toString(),
          currency: rate.currency,
          description: rate.description || "",
        })
      } else {
        setFormData({
          pickup_area_id: "",
          dropoff_area_id: "",
          price: "",
          currency: "ALL",
          description: "",
        })
      }
      setErrors({})
    }
  }, [open, mode, rate])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.pickup_area_id) {
      newErrors.pickup_area_id = "Pickup location is required"
    }
    if (!formData.dropoff_area_id) {
      newErrors.dropoff_area_id = "Dropoff location is required"
    }
    if (formData.pickup_area_id === formData.dropoff_area_id) {
      newErrors.dropoff_area_id = "Dropoff location must be different from pickup location"
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = "Price must be a positive number"
    }
    if (!formData.currency) {
      newErrors.currency = "Currency is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      const data = {
        pickup_area_id: parseInt(formData.pickup_area_id),
        dropoff_area_id: parseInt(formData.dropoff_area_id),
        price: parseFloat(formData.price),
        currency: formData.currency,
        description: formData.description.trim() || undefined,
      }

      if (mode === "create") {
        setIsCreating(true)
        await axiosInstance.post("/route-area-rates", data)
        setIsCreating(false)
        toast("Fixed rate created successfully", {
          description: "Your fixed rate was added successfully!"
        })
      } else if (mode === "edit" && rate) {
        setIsUpdating(true)
        await axiosInstance.patch(`/route-area-rates/${rate.id}`, data)
        setIsUpdating(false)
        toast("Fixed rate updated successfully", {
          description: "Your fixed rate was updated successfully!"
        })
      }
      onSuccess()
    } catch (error) {
      setIsCreating(false)
      setIsUpdating(false)
      toast(`Error ${mode === "create" ? "creating" : "updating"} route area rate`, {
        description: "Please try again or contact support if the problem persists."
      })
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }))
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {mode === "create" ? "Create Fixed Rate" : "Edit Fixed Rate"}
            </DialogTitle>
            <DialogDescription>
              {mode === "create"
                ? "Add a new fixed rate to your system."
                : "Update the fixed rate details."}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 pr-4 overflow-auto">
            <form id="route-area-rate-form" onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pickup_area_id">Pickup Area</Label>
                  <Select
                    value={formData.pickup_area_id}
                    onValueChange={(value) => handleInputChange("pickup_area_id", value)}
                  >
                    <SelectTrigger className={errors.pickup_area_id ? "border-red-500" : ""}>
                      <SelectValue placeholder="Select pickup location" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceLocations.map((location) => (
                        <SelectItem key={location.id} value={location.id.toString()}>
                          <div className="flex items-center gap-2">
                            <span>{location.name}</span>
                            {location.type !== "POINT" && (
                              <Badge variant="secondary" className="text-[10px] px-1 py-0">
                                {location.type}
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.pickup_area_id && <p className="text-sm text-red-500">{errors.pickup_area_id}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dropoff_area_id">Dropoff Area</Label>
                  <Select
                    value={formData.dropoff_area_id}
                    onValueChange={(value) => handleInputChange("dropoff_area_id", value)}
                  >
                    <SelectTrigger className={errors.dropoff_area_id ? "border-red-500" : ""}>
                      <SelectValue placeholder="Select dropoff location" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceLocations.map((location) => (
                        <SelectItem key={location.id} value={location.id.toString()}>
                          <div className="flex items-center gap-2">
                            <span>{location.name}</span>
                            {location.type !== "POINT" && (
                              <Badge variant="secondary" className="text-[10px] px-1 py-0">
                                {location.type}
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.dropoff_area_id && <p className="text-sm text-red-500">{errors.dropoff_area_id}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Price</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter price"
                    value={formData.price}
                    onChange={(e) => handleInputChange("price", e.target.value)}
                    className={errors.price ? "border-red-500" : ""}
                  />
                  {errors.price && <p className="text-sm text-red-500">{errors.price}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => handleInputChange("currency", value)}
                  >
                    <SelectTrigger className={errors.currency ? "border-red-500" : ""}>
                      <SelectValue placeholder="Currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">ALL</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>

                    </SelectContent>
                  </Select>
                  {errors.currency && <p className="text-sm text-red-500">{errors.currency}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Enter description"
                  className="resize-none"
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                />
              </div>
            </form>
          </ScrollArea>

          <DialogFooter className="mt-4 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" form="route-area-rate-form" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "create" ? "Create Rate" : "Update Rate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}